import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, CheckCircle2, XCircle, Loader2, ListTodo, TrendingUp } from 'lucide-react';
import { QueueStats } from '@/hooks/useQueueMonitor';

interface QueueStatusCardsProps {
  stats: QueueStats | undefined;
  isLoading?: boolean;
}

/**
 * Componente que exibe cards com estatísticas da fila de processamento
 */
export function QueueStatusCards({ stats, isLoading }: QueueStatusCardsProps) {
  const cards = [
    {
      title: 'Pendentes',
      value: stats?.pending || 0,
      icon: ListTodo,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
    },
    {
      title: 'Processando',
      value: stats?.processing || 0,
      icon: Loader2,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      animate: stats && stats.processing > 0 ? 'animate-spin' : '',
    },
    {
      title: 'Completados (24h)',
      value: stats?.completed24h || 0,
      icon: CheckCircle2,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Falhas (24h)',
      value: stats?.failed24h || 0,
      icon: XCircle,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
    },
    {
      title: 'Taxa de Sucesso',
      value: stats ? `${stats.successRate.toFixed(1)}%` : '0%',
      icon: TrendingUp,
      color: stats && stats.successRate >= 95 ? 'text-green-500' : stats && stats.successRate >= 90 ? 'text-yellow-500' : 'text-red-500',
      bgColor: stats && stats.successRate >= 95 ? 'bg-green-500/10' : stats && stats.successRate >= 90 ? 'bg-yellow-500/10' : 'bg-red-500/10',
    },
    {
      title: 'Tempo Médio',
      value: stats ? `${stats.avgProcessingTime.toFixed(1)}s` : '0s',
      icon: Clock,
      color: stats && stats.avgProcessingTime < 30 ? 'text-green-500' : stats && stats.avgProcessingTime < 60 ? 'text-yellow-500' : 'text-red-500',
      bgColor: stats && stats.avgProcessingTime < 30 ? 'bg-green-500/10' : stats && stats.avgProcessingTime < 60 ? 'bg-yellow-500/10' : 'bg-red-500/10',
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            <div className={`p-2 rounded-lg ${card.bgColor}`}>
              <card.icon className={`h-4 w-4 ${card.color} ${card.animate || ''}`} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className={`text-2xl font-bold ${card.color}`}>
                {card.value}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
